import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';
import i18n from '../init/i18n';

export default class extends Controller {
  static targets = ['field', 'destroy'];
  static values = {
    maxItems: { type: Number, default: null },
    turboFrameId: String,
    turboFrameUrl: String,
  };

  connect() {
    if (!this.fieldTarget.tomselect) {
      this.selectInstance = new TomSelect(
        this.fieldTarget,
        this._settingsFactory(this.fieldTarget)
      );
      this.observer = new MutationObserver((mutationList, _) =>
        this._onMutation(mutationList)
      );
      this.observer.observe(this.fieldTarget, {
        attributeFilter: ['disabled'],
      });

      this.fieldTarget.addEventListener('change', this._onChange.bind(this));

      this.fieldTarget.addEventListener('clear', (event) => {
        this._onClear(event);
      });

      this._setSelectedValueFromDataset();

      this.fieldTarget.dispatchEvent(new Event('tomSelectInitialized'));
    }
  }

  _setSelectedValueFromDataset() {
    const selectedDataRaw = this.fieldTarget.dataset.selectedValue;
    if (!selectedDataRaw || !this.selectInstance) return;

    let selectedData;
    try {
      selectedData = JSON.parse(selectedDataRaw);
    } catch (e) {
      console.error('Invalid JSON in data-selected-value', e);
      return;
    }

    if (!selectedData.id) return;

    if (!this.selectInstance.options[selectedData.id]) {
      this.selectInstance.addOption({
        value: selectedData.id,
        text: selectedData.text || selectedData.id,
      });
    }

    this.selectInstance.setValue(selectedData.id, true);
  }

  _onChange(event) {
    const turboFrame = this.turboFrameIdValue;
    const selectedValue = this.fieldTarget.value;
    const url = `${this.turboFrameUrlValue}${selectedValue}`;

    if (turboFrame && selectedValue && url) {
      Turbo.visit(url, { frame: turboFrame });
    }
  }

  disconnect() {
    const value = this.fieldTarget.value;

    this.observer.disconnect();
    this.fieldTarget.tomselect.destroy();

    // necessary to persist value because
    // this.fieldTarget.tomselect.destroy() sets the value to null
    this.fieldTarget.value = value;
  }

  reinit() {
    this.fieldTarget.tomselect.setValue(null);
    this.fieldTarget.tomselect.clear();
    this.fieldTarget.tomselect.clearOptions();
    this.fieldTarget.value = null;

    this.disconnect();

    var length = this.fieldTarget.options.length;
    for (let i = length - 1; i >= 0; i--) {
      this.fieldTarget.options.remove(i);
    }

    this.connect();
  }

  _settingsFactory(element) {
    const ajaxUrl = element.dataset.ajaxUrl;
    const ajaxTextField = element.dataset.ajaxTextField;
    const ajaxValueField = element.dataset.ajaxValueField;
    const ajaxMatcher = element.dataset.ajaxSearchMatcher;
    const ajaxAdditionalFilters = element.dataset.ajaxAdditionalFilters
      ? JSON.parse(element.dataset.ajaxAdditionalFilters)
      : [];

    const formatAjaxData = (json, valueField, textField) => {
      return json.map((entry) => ({
        value: entry[valueField],
        text: entry[textField],
      }));
    };

    const fetchQuery = (values, field) => {
      const matcher = `${field}_in`;
      const query = values.map((value) => `q[${matcher}][]=${value}`);

      return query.join('&');
    };

    const computedMaxItems = () => {
      if (this.maxItemsValue != null) {
        return this.maxItemsValue;
      }
      if (this.fieldTarget.multiple) {
        return null;
      }

      return 1;
    };

    const settingsStore = {
      destroy: {
        onDelete: (_, _event) => {
          this.destroyTarget.value = true;
        },
        onChange: (value) => {
          if (value === '') {
            return;
          }
          this.destroyTarget.value = false;
        },
      },
      search: {
        create: true,
        persist: false,
        render: {
          option_create: function (data, escape) {
            return `<div class="create">${i18n.t('search')} <strong>${escape(
              data.input
            )}</strong>&hellip;</div>`;
          },
        },
      },
      create: {
        create: (input) => {
          this.selectInstance.removeOption('');
          return { value: '', text: input };
        },
        persist: false,
      },
      ajax: {
        valueField: 'value',
        labelField: 'text',
        searchField: 'text',
        preload: true,
        shouldLoad: (query) => {
          return true;
        },
        firstUrl: function (query) {
          const url = new URL(ajaxUrl);
          url.searchParams.append(`q[${ajaxTextField}_${ajaxMatcher}]`, query);

          ajaxAdditionalFilters.forEach((filter) => {
            const [predicate, value] = filter;
            if (Array.isArray(value)) {
              value.forEach((v) => {
                url.searchParams.append(`q[${predicate}][]`, v);
              });
            } else {
              url.searchParams.append(`q[${predicate}]`, value);
            }
          });

          return url;
        },
        load: function (query, callback) {
          const url = this.getUrl(query);
          fetch(url, {
            headers: {
              Accept: 'application/json',
            },
          })
            .then((response) => response.json())
            .then((data) => {
              if (data.next_page) {
                const nextUrl = `${url}&page=${data.next_page}`;
                this.setNextUrl(query, nextUrl);
              }

              callback(
                formatAjaxData(data.records, ajaxValueField, ajaxTextField)
              );
            })
            .catch((e) => {
              callback();
            });
        },
      },
    };

    const settings = {
      hidePlaceholder: true,
      maxOptions: null,
      onItemAdd: function () {
        this.setTextboxValue('');
      },
      maxItems: computedMaxItems(),
      plugins: {
        remove_button: {
          title: 'remove',
        },
      },

      render: {
        option_create: function (data, escape) {
          return `<div class="create"><span class="badge badge-primary-100">${i18n.t(
            'add'
          )}</span> <strong>
            ${escape(data.input)}
            </strong>&hellip;</div>`;
        },
        no_results: function (data, escape) {
          return `<div class="no-results">${i18n.t('no_results')}</div>`;
        },
        loading_more: function (data, escape) {
          return `<div class="loading">${i18n.t('loading_more')}</div>`;
        },
        no_more_results: function (data, escape) {
          return `<div class="loading">${i18n.t('no_more_results')}</div>`;
        },
      },
    };

    if (element.classList.contains('tom-select-ajax')) {
      Object.assign(settings, settings, {
        ...settingsStore.ajax,
        plugins: { ...settings.plugins, virtual_scroll: {} },
      });
    }
    if (element.classList.contains('tom-select-create')) {
      Object.assign(settings, settings, settingsStore.create);
    }
    if (element.classList.contains('tom-select-destroy')) {
      Object.assign(settings, settings, settingsStore.destroy);
    }
    if (element.classList.contains('tom-select-search')) {
      Object.assign(settings, settings, settingsStore.search);
    }

    return settings;
  }

  _onClear(event) {
    this.selectInstance.sync();
  }

  _onMutation(mutationList) {
    for (const mutation of mutationList) {
      if (this.fieldTarget.disabled && !this.selectInstance.isDisabled) {
        this.selectInstance.disable();
      } else if (!this.fieldTarget.disabled && this.selectInstance.isDisabled) {
        this.selectInstance.enable();
      }
    }
  }
}
